/* ──────────────────────────────────────────────────────────────
   src/api/reunionReglas.api.ts
   Reglas de agendación de reuniones – llamadas HTTP (axios)
   ────────────────────────────────────────────────────────────── */
import api from './axios';

/** Valores posibles – ajusta a tus enums reales del backend */
export const REUNION_TIPO_VALUES = [
  'familia_inicial',
  'familia_seguimiento',
  'familia_otros',
  'staff_interno',
  'profesor',
  'otro',
] as const;

export const REUNION_ROL_DESTINO_VALUES = [
  'att_familias',
  'att_alumnado',
  'att_profesorado',
  'admisiones',
  'hr',
  'direccion',
  'coordinacion',
  'otro',
] as const;

export const REUNION_CANAL_VALUES = ['online', 'presencial', 'telefono', 'indistinto'] as const;

export type ReunionTipoReunion = (typeof REUNION_TIPO_VALUES)[number];
export type ReunionRolStaffDestino = (typeof REUNION_ROL_DESTINO_VALUES)[number];
export type ReunionCanalPreferente = (typeof REUNION_CANAL_VALUES)[number];

export interface ReunionReglaAgendacion {
  id: number;
  tipoReunion: ReunionTipoReunion | string;
  rolStaffDestino: ReunionRolStaffDestino | string;
  canalPreferente: ReunionCanalPreferente | string | null;
  diasDesdeEventoMin: number | null;
  diasDesdeEventoMax: number | null;
  duracionMinutos: number | null;
  activa: boolean;
  nombre?: string | null;
  idUsuario?: number | null;
  idCentro?: number | null;
  esRecurrente?: boolean;
  frecuenciaSemanas?: number | null;
  usarCanalDeClase?: boolean;
  idServicioExterno?: number | null;
  servicioId?: number | null;
  esWc1?: boolean;
  esWc2?: boolean;
  calculoFecha?: 'relativo_clase' | 'semana_siguiente' | 'dias_desde_evento' | null;
  minutosRelativoClase?: number | null;
  usuario?: { id: number; nombreCompleto: string } | null;
  centro?: { id: number; nombre: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Filtros de listado */
export interface ReunionReglaFilters {
  tipoReunion?: string;
  rolStaffDestino?: string;
  canalPreferente?: string;
  activa?: boolean;
  nombre?: string;
  idUsuario?: number;
  idCentro?: number;
  esRecurrente?: boolean;
  servicioId?: number;
}

/** Payload de creación/edición */
export interface ReunionReglaSavePayload {
  tipoReunion: string;
  rolStaffDestino: string;
  canalPreferente: string | null;
  diasDesdeEventoMin: number | null;
  diasDesdeEventoMax: number | null;
  duracionMinutos: number | null;
  activa: boolean;
  nombre?: string | null;
  idUsuario?: number | null;
  idCentro?: number | null;
  esRecurrente?: boolean;
  frecuenciaSemanas?: number | null;
  usarCanalDeClase?: boolean;
  idServicioExterno?: number | null;
  servicioId?: number | null;
  esWc1?: boolean;
  esWc2?: boolean;
  calculoFecha?: 'relativo_clase' | 'semana_siguiente' | 'dias_desde_evento' | null;
  minutosRelativoClase?: number | null;
}

/* Helper para mapear la respuesta del backend a nuestro modelo laxo */
function mapRegla(raw: any): ReunionReglaAgendacion {
  if (!raw) {
    throw new Error('Regla inválida');
  }
  return {
    id: Number(raw.id),
    tipoReunion: raw.tipoReunion ?? raw.tipo ?? raw.tipo_reunion ?? 'otro',
    rolStaffDestino: raw.rolStaffDestino ?? raw.rolDestino ?? raw.rol_staff_destino ?? 'otro',
    canalPreferente: raw.canalPreferente ?? raw.canal ?? raw.canal_preferente ?? null,
    diasDesdeEventoMin: raw.diasDesdeEventoMin ?? raw.diasMin ?? raw.dias_desde_evento_min ?? null,
    diasDesdeEventoMax: raw.diasDesdeEventoMax ?? raw.diasMax ?? raw.dias_desde_evento_max ?? null,
    duracionMinutos: raw.duracionMinutos ?? raw.duracion ?? raw.duracion_minutos ?? null,
    activa: Boolean(raw.activa ?? raw.activo ?? raw.isActive ?? true),
    nombre: raw.nombre ?? null,
    idUsuario: raw.idUsuario ?? raw.id_usuario ?? null,
    idCentro: raw.idCentro ?? raw.id_centro ?? null,
    esRecurrente: Boolean(raw.esRecurrente ?? raw.es_recurrente ?? false),
    frecuenciaSemanas: raw.frecuenciaSemanas ?? raw.frecuencia_semanas ?? null,
    usarCanalDeClase: Boolean(raw.usarCanalDeClase ?? raw.usar_canal_de_clase ?? false),
    idServicioExterno: raw.idServicioExterno ?? raw.id_servicio_externo ?? null,
    servicioId: raw.servicioId ?? raw.servicio_id ?? null,
    esWc1: Boolean(raw.esWc1 ?? raw.es_wc1 ?? false),
    esWc2: Boolean(raw.esWc2 ?? raw.es_wc2 ?? false),
    calculoFecha: raw.calculoFecha ?? raw.calculo_fecha ?? null,
    minutosRelativoClase: raw.minutosRelativoClase ?? raw.minutos_relativo_clase ?? null,
    usuario: raw.usuario ? { id: raw.usuario.id, nombreCompleto: raw.usuario.nombreCompleto ?? raw.usuario.nombre_completo } : null,
    centro: raw.centro ? { id: raw.centro.id, nombre: raw.centro.nombre ?? raw.centro.nombre_centro } : null,
    createdAt: raw.createdAt ?? raw.created_at,
    updatedAt: raw.updatedAt ?? raw.updated_at,
  };
}

const BASE_URL = '/reunion-regla-agendacion';

export async function fetchReunionReglas(
  filters: ReunionReglaFilters = {},
): Promise<ReunionReglaAgendacion[]> {
  const { data } = await api.get(BASE_URL, {
    params: filters,
  });
  return (data ?? []).map(mapRegla);
}

export async function fetchReunionRegla(id: number): Promise<ReunionReglaAgendacion> {
  const { data } = await api.get(`${BASE_URL}/${id}`);
  return mapRegla(data);
}

export async function createReunionRegla(
  payload: ReunionReglaSavePayload,
): Promise<ReunionReglaAgendacion> {
  const { data } = await api.post(BASE_URL, payload);
  return mapRegla(data);
}

export async function updateReunionRegla(
  id: number,
  payload: ReunionReglaSavePayload,
): Promise<ReunionReglaAgendacion> {
  const { data } = await api.put(`${BASE_URL}/${id}`, payload);
  return mapRegla(data);
}

export async function deleteReunionRegla(id: number): Promise<void> {
  await api.delete(`${BASE_URL}/${id}`);
}
